// ============================================================================
// RemunerationSummaryCard — carte de synthese en tete de l'onglet Gestion
// ============================================================================
// Parite PoC CadreCard (components/salary/cadres-overview.tsx @ SHA 7e40e4a) :
// salaire, evol 1 an / 3 ans (% + $), boni (cible italique* + verse), avantages,
// remuneration totale mise en avant, pied classe.echelon, mention plein boni.
// Lecture seule — zero mutation, zero log de montant (PII remuneration).
// Regle #4 : zero emoji.

import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { formatCad, formatPctPlain, formatSignedCad, formatSignedPct } from '../salary/format';
import { Badge } from './ui';

export type RemunerationSummaryProps = {
  configured: boolean;
  salaire: number | null;
  augmentationPct: number | null;
  cumulAugmentationPct: number | null;
  aug1yAmount: number | null;
  aug3yAmount: number | null;
  boni: number;
  boniCible: number;
  boniCiblePct: number | null;
  avantages: number;
  total: number | null;
  classLabel: string | null;
  echelon: number;
  maxEchelon: number;
  offGrid: boolean;
  /** Plafond d'echelon du niveau — calcule par le parent, jamais derive localement. */
  atCap: boolean;
};

function toneClass(n: number | null): string {
  if (n === null) return 'text-slate-500 dark:text-slate-400';
  return n >= 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-400';
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={strong ? 'font-medium text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}>
        {label}
      </span>
      <span className={`tabular-nums ${strong ? 'text-body font-semibold text-slate-800 dark:text-slate-100' : 'font-medium text-slate-700 dark:text-slate-200'}`}>
        {value}
      </span>
    </div>
  );
}

function GrowthRow({
  label,
  pct,
  amt,
  locale,
}: {
  label: string;
  pct: number | null;
  amt: number | null;
  locale: string;
}) {
  if (pct === null && amt === null) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-meta">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`flex gap-2 font-medium tabular-nums ${toneClass(pct ?? amt)}`}>
        <span>{pct !== null ? formatSignedPct(pct, locale) : '—'}</span>
        <span>{amt !== null ? formatSignedCad(amt, locale) : '—'}</span>
      </span>
    </div>
  );
}

export function RemunerationSummaryCard(props: RemunerationSummaryProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const {
    configured,
    salaire,
    augmentationPct,
    cumulAugmentationPct,
    aug1yAmount,
    aug3yAmount,
    boni,
    boniCible,
    boniCiblePct,
    avantages,
    total,
    classLabel,
    echelon,
    maxEchelon,
    offGrid,
    atCap,
  } = props;

  return (
    <div className={`${GS_COMPONENTS.card} flex flex-col gap-2 p-4`}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">
        {t('gest:management.summary.title')}
      </p>

      {!configured || total === null ? (
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:management.summary.notConfigured')}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-1 text-field">
            <Row label={t('gest:management.summary.salary')} value={formatCad(salaire ?? 0, locale)} />
            <div className="flex flex-col gap-0.5">
              <GrowthRow
                label={t('gest:management.summary.var1y')}
                pct={augmentationPct}
                amt={aug1yAmount}
                locale={locale}
              />
              <GrowthRow
                label={t('gest:management.summary.var3y')}
                pct={cumulAugmentationPct}
                amt={aug3yAmount}
                locale={locale}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-slate-500 dark:text-slate-400">{t('gest:management.summary.bonus')}</span>
              <span className="flex items-center gap-2 tabular-nums">
                {boniCible > 0 ? (
                  <span className="text-meta italic text-slate-500 dark:text-slate-400">
                    *{boniCiblePct !== null ? `${formatPctPlain(boniCiblePct, locale)} · ` : ''}
                    {formatCad(boniCible, locale)}
                  </span>
                ) : null}
                <span className="font-medium text-slate-700 dark:text-slate-200">{formatCad(boni, locale)}</span>
              </span>
            </div>
            <Row label={t('gest:management.summary.benefits')} value={formatCad(avantages, locale)} />
            <div className="my-0.5 border-t border-slate-200 dark:border-slate-700" />
            <Row label={t('gest:management.summary.total')} value={formatCad(total, locale)} strong />
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-meta text-slate-500 dark:text-slate-400">
              <span>
                {classLabel ?? t('gest:management.summary.classUnknown')} ·{' '}
                {offGrid
                  ? t('gest:management.summary.offGrid')
                  : t('gest:management.summary.echelon', { echelon, max: maxEchelon })}
              </span>
              {atCap && !offGrid ? (
                <Badge tone="neutral">{t('gest:management.summary.atCap')}</Badge>
              ) : null}
            </p>
            {boniCible > 0 ? (
              <p className="text-meta italic text-slate-500 dark:text-slate-400">
                {t('gest:management.summary.fullBonus')}{' '}
                <span className="font-medium not-italic tabular-nums text-slate-800 dark:text-slate-100">
                  {formatCad(total + boniCible - boni, locale)}
                </span>
              </p>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
